import {
  CLR_STRATEGIC_ACCOUNT_THRESHOLD,
  MASS_CLR_GATE_COMMENT_SLOTS,
  MASS_CLR_PREPARER_COMMENT_SLOTS,
} from '@repo/constants'

// Pure client-side derivations over run lines as the backend returns them (numeric columns are
// strings, per drizzle). Buckets, the swing-vs-trend advisory and the run stats all derive from
// the line row itself — the server owns the data, this file owns how the workbench reads it.

/** A limit swing below this magnitude needs no defence against the sales trend. */
export const MASS_CLR_TREND_SWING_PERCENT = 15

/** The line fields the derivations read — structural, so the inferred hono row type satisfies it
 *  without coupling this file to the hooks. */
export type MassClrLineInput = {
  id: number
  currentCreditLimit: string | null
  currentRiskClass: string | null
  recommendedCreditLimit: string | null
  recommendedCreditLimitReason: string | null
  recommendedRiskClass: string | null
  proposedCreditLimit: string | null
  proposedRiskClass: string | null
  reasonForDeviation: string | null
  flag: 'risk_class' | 'limit_swing' | null
  isRemoved: boolean
  isExcluded: boolean
  isInactive: boolean
  withdrawnToClrId: number | null
  hasSecurityDeposit: boolean
  salesTrendVariancePercent: string | null
}

export const MASS_CLR_LINE_BUCKETS = [
  'clean',
  'flagged',
  'unchanged',
  'deviated',
  'withdrawn',
  'excluded',
  'removed',
] as const
export type MassClrLineBucket = (typeof MASS_CLR_LINE_BUCKETS)[number]

/** The workbench's quick filters: the six buckets plus derived views. `commented`/`uncommented`
 *  read the chain actors' per-line comment threads (current cycle) — the chain screens' working
 *  axis, and the preparer's "what did the approver flag" bucket after a send-back. */
export const MASS_CLR_LINE_FILTERS = [
  'all',
  'flagged',
  'against_trend',
  'unchanged',
  'deviated',
  'clean',
  'inactive',
  'commented',
  'uncommented',
  'withdrawn',
  'excluded',
  'removed',
] as const
export type MassClrLineFilter = (typeof MASS_CLR_LINE_FILTERS)[number]

export type MassClrLineState = {
  bucket: MassClrLineBucket
  /** Null for withdrawn/removed lines — nothing is written for them. */
  proposedCreditLimit: number | null
  proposedRiskClass: string | null
  isDeviation: boolean
  /** Split like the single-CLR table: the limit and the risk class each warn on their own
   *  account — a review can move the class while leaving the limit alone, and vice versa. */
  isLimitDeviation: boolean
  isRiskClassDeviation: boolean
  /** No applicable recommendation (none computed, or it reached the $250k cap): the current
   *  limit stands and nothing is written unless the preparer deviates. */
  isUnchanged: boolean
  isCapped: boolean
  hasNoRecommendation: boolean
}

export const toNumber = (value: string | number | null | undefined): number | null =>
  value == null ? null : Number(value)

/** The stored variance is a fraction (0.12 = +12%), like everywhere else in the credit module —
 *  this is the percent the workbench's thresholds and labels work in. */
export function getSalesTrendPercent(line: MassClrLineInput): number | null {
  const fraction = toNumber(line.salesTrendVariancePercent)
  return fraction == null ? null : fraction * 100
}

/**
 * What a line resolves to. Nothing in a run can block it: a line either takes the
 * recommendation, carries a deviation the preparer wrote, or is left unchanged because the
 * recommendation cannot be applied (none computed, or ≥ the $250k strategic cap). "Left
 * unchanged" means exactly that: the current limit stands and nothing is written on approval.
 */
export function getMassClrLineState(line: MassClrLineInput): MassClrLineState {
  if (line.withdrawnToClrId != null) {
    return {
      bucket: 'withdrawn',
      proposedCreditLimit: null,
      proposedRiskClass: null,
      isDeviation: false,
      isLimitDeviation: false,
      isRiskClassDeviation: false,
      isUnchanged: false,
      isCapped: false,
      hasNoRecommendation: false,
    }
  }
  if (line.isExcluded) {
    return {
      bucket: 'excluded',
      proposedCreditLimit: null,
      proposedRiskClass: null,
      isDeviation: false,
      isLimitDeviation: false,
      isRiskClassDeviation: false,
      isUnchanged: false,
      isCapped: false,
      hasNoRecommendation: false,
    }
  }
  if (line.isRemoved) {
    return {
      bucket: 'removed',
      proposedCreditLimit: null,
      proposedRiskClass: null,
      isDeviation: false,
      isLimitDeviation: false,
      isRiskClassDeviation: false,
      isUnchanged: false,
      isCapped: false,
      hasNoRecommendation: false,
    }
  }

  const recommended = toNumber(line.recommendedCreditLimit)
  const hasNoRecommendation = recommended === null
  const isCapped = recommended !== null && recommended >= CLR_STRATEGIC_ACCOUNT_THRESHOLD
  const isApplicable = !hasNoRecommendation && !isCapped

  const baseCreditLimit = isApplicable ? recommended! : (toNumber(line.currentCreditLimit) ?? 0)
  const baseRiskClass = isApplicable ? line.recommendedRiskClass : line.currentRiskClass

  // A stored proposal only counts as a deviation for the field it actually moves — a saved
  // value equal to what the system would write is not a deviation of that field.
  const isLimitDeviation =
    line.proposedCreditLimit != null && toNumber(line.proposedCreditLimit) !== baseCreditLimit
  const isRiskClassDeviation =
    line.proposedRiskClass != null && line.proposedRiskClass !== baseRiskClass
  const isDeviation = isLimitDeviation || isRiskClassDeviation
  const proposedCreditLimit = toNumber(line.proposedCreditLimit) ?? baseCreditLimit
  const proposedRiskClass = line.proposedRiskClass ?? baseRiskClass

  const base = {
    proposedCreditLimit,
    proposedRiskClass,
    isDeviation,
    isLimitDeviation,
    isRiskClassDeviation,
    isCapped,
    hasNoRecommendation,
  }

  if (isDeviation) return { ...base, bucket: 'deviated', isUnchanged: false }
  if (!isApplicable) return { ...base, bucket: 'unchanged', isUnchanged: true }
  if (line.flag) return { ...base, bucket: 'flagged', isUnchanged: false }
  return { ...base, bucket: 'clean', isUnchanged: false }
}

export type MassClrTrendCheck = {
  key: 'flat' | 'supported' | 'against'
  movePercent: number
}

/**
 * Whether a proposed limit swings with or against the account's own 12-month revenue trend.
 * Advisory only — a question an approver can ask, never a gate.
 */
export function checkTrend(line: MassClrLineInput, proposedCreditLimit: number): MassClrTrendCheck {
  const currentCreditLimit = toNumber(line.currentCreditLimit) ?? 0
  const trendPercent = getSalesTrendPercent(line)
  const movePercent =
    currentCreditLimit === 0
      ? 0
      : ((proposedCreditLimit - currentCreditLimit) / currentCreditLimit) * 100

  if (Math.abs(movePercent) < MASS_CLR_TREND_SWING_PERCENT || trendPercent == null) {
    return { key: 'flat', movePercent }
  }
  if (movePercent > 0 && trendPercent < -10) return { key: 'against', movePercent }
  if (movePercent < 0 && trendPercent > 10) return { key: 'against', movePercent }
  return { key: 'supported', movePercent }
}

export function matchesLineFilter(
  line: MassClrLineInput,
  filter: MassClrLineFilter,
  commentedLineIds?: Set<number>,
): boolean {
  const state = getMassClrLineState(line)
  const isInRun =
    state.bucket !== 'removed' && state.bucket !== 'withdrawn' && state.bucket !== 'excluded'

  if (filter === 'all') return isInRun
  if (filter === 'against_trend') {
    return isInRun && checkTrend(line, state.proposedCreditLimit!).key === 'against'
  }
  // Inactive is an axis, not a bucket: an inactive deviated line shows in both views.
  if (filter === 'inactive') return isInRun && line.isInactive
  if (filter === 'commented') return isInRun && (commentedLineIds?.has(line.id) ?? false)
  if (filter === 'uncommented') return isInRun && !(commentedLineIds?.has(line.id) ?? false)
  return state.bucket === filter
}

export type MassClrRunStats = {
  counts: Record<MassClrLineFilter, number>
  /** Lines still in the run (not removed/withdrawn/excluded). */
  inRun: number
  currentExposure: number
  proposedExposure: number
  exposureDelta: number
  distinctDeviationReasons: number
}

export function computeRunStats(
  lines: MassClrLineInput[],
  commentedLineIds?: Set<number>,
): MassClrRunStats {
  const counts: MassClrRunStats['counts'] = {
    all: 0,
    flagged: 0,
    against_trend: 0,
    unchanged: 0,
    deviated: 0,
    clean: 0,
    inactive: 0,
    commented: 0,
    uncommented: 0,
    withdrawn: 0,
    excluded: 0,
    removed: 0,
  }
  let inRun = 0
  let currentExposure = 0
  let proposedExposure = 0
  const reasons = new Set<string>()

  for (const line of lines) {
    const state = getMassClrLineState(line)
    counts[state.bucket]++
    if (state.bucket === 'removed' || state.bucket === 'withdrawn' || state.bucket === 'excluded') {
      continue
    }

    inRun++
    if (line.isInactive) counts.inactive++
    currentExposure += toNumber(line.currentCreditLimit) ?? 0
    proposedExposure += state.proposedCreditLimit!
    if (checkTrend(line, state.proposedCreditLimit!).key === 'against') counts.against_trend++
    if (commentedLineIds?.has(line.id)) counts.commented++
    else counts.uncommented++
    if (state.bucket === 'deviated' && line.reasonForDeviation) {
      reasons.add(line.reasonForDeviation.trim())
    }
  }

  counts.all = inRun
  return {
    counts,
    inRun,
    currentExposure,
    proposedExposure,
    exposureDelta: proposedExposure - currentExposure,
    distinctDeviationReasons: reasons.size,
  }
}

/**
 * The line ids commented in the CURRENT cycle, by anyone. A cycle starts at the latest preparer
 * submission (`submitted` action); only a submission bumps the version now, but historical runs
 * carry non-submit bumps from the days of the reviewer slot, so the cycle is keyed on the action
 * rather than on the exact current version. Drives the Commented tab and the comment filter.
 */
export function currentCycleCommentedLineIds(
  comments: { lineId: number; versionNumber: number }[],
  actions: { action: string; versionNumber: number }[],
): Set<number> {
  let cycleStartVersion = 0
  for (const action of actions) {
    if (action.action === 'submitted' && action.versionNumber > cycleStartVersion) {
      cycleStartVersion = action.versionNumber
    }
  }
  const lineIds = new Set<number>()
  for (const comment of comments) {
    if (comment.versionNumber >= cycleStartVersion) lineIds.add(comment.lineId)
  }
  return lineIds
}

/**
 * The line ids that satisfy the approver's gate: commented in the current cycle from an approver
 * slot (`MASS_CLR_GATE_COMMENT_SLOTS`). The preparer's notes are context for the approver, not
 * coverage — a run cannot be approved on the strength of the notes its preparer wrote. Client
 * twin of the server's comment query in `applyMassClrDecision`.
 */
export function gateCommentedLineIds(
  comments: { lineId: number; versionNumber: number; actedAsSlot: string | null }[],
  actions: { action: string; versionNumber: number }[],
): Set<number> {
  const gateSlots: readonly string[] = MASS_CLR_GATE_COMMENT_SLOTS
  return currentCycleCommentedLineIds(
    comments.filter((comment) => comment.actedAsSlot && gateSlots.includes(comment.actedAsSlot)),
    actions,
  )
}

/**
 * The line ids that satisfy the preparer's submit gate: commented from a preparer slot
 * (`MASS_CLR_PREPARER_COMMENT_SLOTS`), in any cycle. Client twin of the server's check in
 * `submitMassClrRun`.
 */
export function preparerCommentedLineIds(
  comments: { lineId: number; actedAsSlot: string | null }[],
): Set<number> {
  const preparerSlots: readonly string[] = MASS_CLR_PREPARER_COMMENT_SLOTS
  return new Set(
    comments
      .filter((comment) => comment.actedAsSlot && preparerSlots.includes(comment.actedAsSlot))
      .map((comment) => comment.lineId),
  )
}
